import React, { useState, useEffect } from 'react';

interface User {
  id: string;
  username: string;
  password?: string;
  category: 'administrator' | 'maintenance' | 'reporting' | 'operator';
  is_active: boolean;
  is_default?: boolean;
  login_timeout_minutes?: number | null;
  last_login?: string;
  created_at?: string;
}

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: User) => void;
  user: User | null;
  mode: 'add' | 'edit';
}

const UserEditModal: React.FC<UserEditModalProps> = ({ isOpen, onClose, onSave, user, mode }) => {
  const [formData, setFormData] = useState<User>({
    id: '',
    username: '',
    password: '',
    category: 'operator',
    is_active: true,
    is_default: false,
    login_timeout_minutes: null
  });
  const [showPassword, setShowPassword] = useState(false);
  const [enableTimeout, setEnableTimeout] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({ ...user, password: '' });
      setShowPassword(false);
      setEnableTimeout(user.login_timeout_minutes !== null && user.login_timeout_minutes !== undefined);
    } else if (mode === 'add') {
      setFormData({
        id: '',
        username: '',
        password: '',
        category: 'operator',
        is_active: true,
        is_default: false,
        login_timeout_minutes: null
      });
      setShowPassword(true);
      setEnableTimeout(false);
    }
    setError('');
  }, [user, mode, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate username
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }

    // Validate password for new users or if password is being changed
    if ((mode === 'add' || formData.password) && formData.password && !/^\d+$/.test(formData.password)) {
      setError('Password must contain only numbers');
      return;
    }

    // Require password for new users (unless it's a default account)
    if (mode === 'add' && !formData.password && !formData.is_default) {
      setError('Password is required for new users');
      return;
    }

    // Validate timeout if enabled
    if (enableTimeout && formData.login_timeout_minutes !== null) {
      if (formData.login_timeout_minutes < 1 || formData.login_timeout_minutes > 480) {
        setError('Timeout must be between 1 and 480 minutes (8 hours)');
        return;
      }
    }

    // Set timeout to null if disabled
    const dataToSave = {
      ...formData,
      login_timeout_minutes: enableTimeout ? formData.login_timeout_minutes : null
    };

    onSave(dataToSave);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'add' ? 'Add New User' : 'Edit User'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
              autoFocus
            />
          </div>

          {(mode === 'add' || showPassword) && !formData.is_default && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password (numbers only)
              </label>
              <input
                type="text"
                value={formData.password || ''}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={mode === 'edit' ? 'Leave blank to keep current password' : 'Enter numeric password'}
                pattern="[0-9]*"
                disabled={formData.is_default}
              />
            </div>
          )}

          {mode === 'edit' && !showPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(true)}
              className="text-sm text-indigo-600 hover:text-indigo-800"
            >
              Change Password
            </button>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as User['category'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="operator">Operator</option>
              <option value="maintenance">Maintenance</option>
              <option value="reporting">Reporting</option>
              <option value="administrator">Administrator</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.is_default || false}
                onChange={(e) => {
                  setFormData({ ...formData, is_default: e.target.checked, password: e.target.checked ? '' : formData.password });
                  if (e.target.checked) {
                    setShowPassword(false);
                  }
                }}
                className="mr-2"
              />
              <label htmlFor="isDefault" className="text-sm font-medium text-gray-700">
                Set as Default Account
              </label>
            </div>
            {formData.is_default && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                This account will auto-login when other users timeout or logout. No password required.
              </p>
            )}
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enableTimeout"
                checked={enableTimeout}
                onChange={(e) => {
                  setEnableTimeout(e.target.checked);
                  if (!e.target.checked) {
                    setFormData({ ...formData, login_timeout_minutes: null });
                  } else {
                    setFormData({ ...formData, login_timeout_minutes: 60 }); // Default 60 minutes
                  }
                }}
                className="mr-2"
              />
              <label htmlFor="enableTimeout" className="text-sm font-medium text-gray-700">
                Enable Login Timeout
              </label>
            </div>
            
            {enableTimeout && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timeout (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  max="480"
                  value={formData.login_timeout_minutes || 60}
                  onChange={(e) => setFormData({ ...formData, login_timeout_minutes: parseInt(e.target.value) || 60 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  User will be logged out after {formData.login_timeout_minutes || 60} minutes of login
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              {mode === 'add' ? 'Add User' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;